import { FunnelFieldLabels } from '../../../constants/deprecated_fieldsManagementConsts';
import { ObjectTypeLabelAndKeyTag } from '../../common/ObjectTypeLabelAndKey';
import { DataTableRow } from '../../common/table/TableTypes';
import { TableTypography } from '../../common/table/utils';
import {
  ConfigurationType,
  DependenciesContentViewType,
  SelectedDependencyWithClearHistoryProps,
} from '../dependencies/types';
import { TableWithHeader, TableWithHeaderProps } from './shared/TableWithHeader';
import { ReactNode, useCallback, useMemo } from 'react';
import { createStableLabelWrapper, markStyles } from '../dependencies/search/LabelWrapperComponent';
import { PossibleSectionInDependencies } from '../dependencies/search/types';
import { TableItemHighlightedText } from '../dependencies/search/highlight/TableItemHighlightedText';

interface FieldTableProps extends Pick<TableWithHeaderProps, 'headerVariant' | 'tableHeader'> {
  items: FieldTableItem[];
  onFieldClick: (props: SelectedDependencyWithClearHistoryProps) => void;
  activeItemId?: string;
  clearHistory?: boolean;
  section?: PossibleSectionInDependencies;
  dataTableType?: string;
}

interface FieldTableItem {
  id: string;
  name: string;
  label: string | ReactNode;
  objectName: string;
  objectLabel: string;
  type: ConfigurationType;
  isRollup: boolean;
}

const fieldsColumns = [
  { field: 'label', headerName: 'Label', width: '140px' },
  { field: 'objectName', headerName: 'Object', width: '110px' },
  { field: 'type', headerName: 'Type', width: '100px' },
];

const renderRows = ({
  items,
  activeItemId,
  section = 'dependencies',
  ObjectNameLabelWrapper,
  dataTableType = 'fields',
}: Pick<FieldTableProps, 'items' | 'activeItemId' | 'section' | 'dataTableType'> & {
  ObjectNameLabelWrapper: ReturnType<typeof createStableLabelWrapper>;
}) => {
  return items.map((row, index) => {
    return {
      id: row.id,
      label: <TableTypography>{row.label}</TableTypography>,
      name: row.name,
      isRowActive: activeItemId === row.id,
      _objectNameStr: row.objectName, //needed in onRowClick
      objectName: (
        <ObjectTypeLabelAndKeyTag
          label={row.objectLabel || row.objectName}
          objectType={row.objectName}
          LabelWrapperComponent={ObjectNameLabelWrapper}
          labelWrapperComponentSx={markStyles}
          labelWrapperComponentProps={{ rowIndex: index }}
        />
      ), //displayed in table
      type: (
        <TableTypography>
          <TableItemHighlightedText
            text={FunnelFieldLabels[row.type as unknown as SweepFieldTypes] ?? row.type}
            field="type"
            section={section}
            tableType={dataTableType}
            rowIndex={index}
          />
        </TableTypography>
      ), //TS is complaining without unknown but the conversion is correct
      isClickable: true,
      isRollup: row.isRollup,
    };
  });
};

export const FieldsTable = ({
  items,
  onFieldClick,
  clearHistory,
  activeItemId,
  section = 'dependencies',
  dataTableType = 'fields',
  ...props
}: FieldTableProps) => {
  // Create stable wrapper component ONCE per table, not per row
  const ObjectNameLabelWrapper = useMemo(
    () =>
      createStableLabelWrapper({
        field: 'objectName',
        section,
        tableType: dataTableType,
      }),
    [section, dataTableType],
  );

  const onRowClick = useCallback(
    (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>, row: DataTableRow) => {
      onFieldClick({
        id: row.id ?? '',
        name: row.name,
        objectName: row._objectNameStr,
        parentType: row.isRollup ? ConfigurationType.rollups : ConfigurationType.fields,
        clearHistory,
        contentType: DependenciesContentViewType.default,
      });
    },
    [onFieldClick, clearHistory],
  );

  return (
    <TableWithHeader
      {...props}
      key="fields"
      columns={fieldsColumns}
      rows={renderRows({ items, activeItemId, section, ObjectNameLabelWrapper, dataTableType })}
      onRowClick={onRowClick}
      dataTableType={dataTableType}
      section={section}
    />
  );
};
